/**
 * Wires up behaviour for Verified popover and the Filter popover.
 *
 * Anchor positioning fallback: for browsers without CSS anchor positioning, manually
 * positions the verified popover below its label and the filters popover below-right of its
 * toggle button. Native popovers sit in the top layer at position:fixed, so
 * getBoundingClientRect() viewport coords map directly — no scroll offset needed.
 */
function initPopovers() {
  const verifiedWrap = document.querySelector('.pos-compatibility-table__verified-wrap');
  const verifiedPopover = document.getElementById('pos-compatibility-verified-popover');
  let activeBadge = null;
  let currentAnchorEl = null;
  const hasCSSAnchor = CSS.supports('anchor-name', '--test');

  const setActiveBadge = (badge) => {
    if (activeBadge === badge) return;
    activeBadge?.removeAttribute('open');
    activeBadge = badge;
    activeBadge?.setAttribute('open', '');
    verifiedWrap?.toggleAttribute('open', badge === null && !!verifiedPopover?.matches(':popover-open'));
  };

  if (verifiedWrap && verifiedPopover) {
    verifiedPopover.addEventListener('toggle', e => {
      verifiedWrap.toggleAttribute('open', e.newState === 'open' && activeBadge === null);
      if (e.newState === 'closed') {
        setActiveBadge(null);
        currentAnchorEl = null;
        verifiedPopover.style.top = '';
        verifiedPopover.style.left = '';
      } else {
        const r = verifiedPopover.getBoundingClientRect();
        const controls = document.querySelector('.pos-compatibility-table__controls');
        const bounds = controls?.getBoundingClientRect();
        const minLeft = bounds ? bounds.left : 0;
        const maxLeft = bounds ? bounds.right - r.width : window.innerWidth - r.width;
        const clampedLeft = Math.min(Math.max(r.left, minLeft), maxLeft);
        if (clampedLeft !== r.left) {
          verifiedPopover.style.left = `${clampedLeft}px`;
        }
      }
    });

    const section = document.querySelector('pos-compatibility-section');

    const positionNear = (el) => {
      currentAnchorEl = el;
      const rect = el.getBoundingClientRect();
      verifiedPopover.style.top = `${rect.bottom + 10}px`;
      verifiedPopover.style.left = `${rect.left}px`;
    };

    // Reposition on scroll for the JS fallback path, and always for row badges
    // (which have no CSS anchor-name and are inside the scrollable page).
    document.addEventListener('scroll', () => {
      if (!verifiedPopover.matches(':popover-open') || !currentAnchorEl) return;
      if (hasCSSAnchor && activeBadge === null) return; // CSS handles the header anchor
      positionNear(currentAnchorEl);
    }, { passive: true, capture: true });

    // Touch / keyboard: click always works
    section?.addEventListener('click', e => {
      const badge = e.target.closest('.pos-compatibility-row__verified-badge');
      if (!badge) return;
      positionNear(badge);
      setActiveBadge(badge);
      if (!verifiedPopover.matches(':popover-open')) verifiedPopover.showPopover();
    });

    if (window.matchMedia('(hover: hover)').matches) {
      let hideTimer;

      const hide = () => {
        hideTimer = setTimeout(() => {
          if (verifiedPopover.matches(':popover-open')) verifiedPopover.hidePopover();
        }, 150);
      };

      section?.addEventListener('mouseover', e => {
        const trigger = e.target.closest('.pos-compatibility-verified-trigger');
        if (!trigger) return;
        if (!trigger.matches('.pos-compatibility-verified-anchor') && activeBadge !== null) return;

        clearTimeout(hideTimer);
        // Skip JS positioning for the header trigger when CSS anchor positioning is supported —
        // the CSS top/left anchor() values handle scroll tracking natively.
        const isRowBadge = trigger.matches('.pos-compatibility-verified-anchor');
        if (!hasCSSAnchor || isRowBadge) {
          positionNear(trigger.querySelector('.pos-compatibility-verified-anchor') ?? trigger);
        }
        setActiveBadge(isRowBadge ? trigger : null);
        if (!verifiedPopover.matches(':popover-open')) verifiedPopover.showPopover();
      });

      section?.addEventListener('mouseout', e => {
        const trigger = activeBadge ?? verifiedWrap;
        if (trigger.contains(e.target) && !trigger.contains(e.relatedTarget)) hide();
      });

      verifiedPopover.addEventListener('mouseenter', () => clearTimeout(hideTimer));
      verifiedPopover.addEventListener('mouseleave', hide);
    }
  }

  if (!CSS.supports('anchor-name', '--test')) {
    const filtersBtn = document.querySelector('.pos-compatibility-filters__btn');
    const filtersPopover = document.getElementById('pos-compatibility-filters-popover');
    if (filtersBtn && filtersPopover) {
      filtersPopover.addEventListener('toggle', () => {
        const rect = filtersBtn.getBoundingClientRect();
        filtersPopover.style.top = `${rect.bottom + 10}px`;
        filtersPopover.style.left = `${rect.right - filtersPopover.offsetWidth}px`;
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopovers);
} else {
  initPopovers();
}

/**
 * Escapes a value for safe insertion into HTML attribute values and text content.
 * @param {*} str - Value to escape; null/undefined coerce to empty string.
 * @returns {string}
 */
function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const VERIFIED_SVG = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10ZM7.19929 3.91214C7.24403 3.85783 7.27764 3.79524 7.29819 3.72794C7.31874 3.66064 7.32584 3.58995 7.31908 3.51991C7.31231 3.44987 7.29181 3.38185 7.25876 3.31972C7.22571 3.2576 7.18074 3.2026 7.12643 3.15786C7.07212 3.11311 7.00953 3.07951 6.94223 3.05895C6.87493 3.0384 6.80424 3.0313 6.7342 3.03807C6.66416 3.04483 6.59613 3.06533 6.53401 3.09838C6.47189 3.13144 6.41689 3.1764 6.37214 3.23071L4.33071 5.70929L3.66 4.70286C3.58047 4.58636 3.4582 4.50592 3.31973 4.47902C3.18126 4.45212 3.03776 4.48091 2.92039 4.55916C2.80302 4.63741 2.72125 4.75879 2.69283 4.89695C2.6644 5.03512 2.69162 5.17892 2.76857 5.29714L3.63286 6.59357C3.66286 6.63857 3.70214 6.69786 3.74214 6.74714C3.78643 6.80286 3.87071 6.89714 4.00714 6.95929C4.17098 7.03321 4.35666 7.04264 4.52714 6.98571C4.63583 6.94818 4.73384 6.88496 4.81286 6.80143C4.85714 6.75643 4.90214 6.70143 4.93643 6.66L7.19929 3.91214Z" fill="currentColor"/></svg>`;

/**
 * Renders a single device row inside a pos-compatibility-table grid.
 */
class PosCompatibilityRow extends HTMLElement {
  /**
   * Populates the row with device data and i18n strings.
   * Must be called before the element is inserted into the DOM.
   * @param {Object} device - Device object from the JSON data block.
   * @param {Object} i18n - Localisation strings.
   */
  init(device, i18n) {
    this.setAttribute('role', 'row');
    this.dataset.searchText = [
      device.title,
      device.vendor,
      device.type,
      ...(device.connectivity || []),
      ...(device.features || [])
    ].filter(Boolean).join(' ').toLowerCase();

    const imageHtml = device.imageUrl
      ? `<img src="${esc(device.imageUrl)}" alt="${esc(device.imageAlt ?? '')}" width="200" height="200" loading="lazy" class="pos-compatibility-row__image">`
      : '';

    const verifiedHtml = device.verified
      ? `<button type="button" class="pos-compatibility-verified-badge pos-compatibility-row__verified-badge pos-compatibility-verified-trigger pos-compatibility-verified-anchor" aria-describedby="pos-compatibility-verified-popover">${VERIFIED_SVG}${esc(i18n.verified)}</button>`
      : '';

    const connectivityItems = (device.connectivity || []).map(item => {
      const parenIdx = item.indexOf('(');
      if (parenIdx !== -1) {
        return `<li>${esc(item.slice(0, parenIdx))}<span class="pos-compatibility-row__connectivity-note">(${esc(item.slice(parenIdx + 1))}</span></li>`;
      }
      return `<li>${esc(item)}</li>`;
    }).join('');

    const featuresItems = (device.features || []).map(item => `<li>${esc(item)}</li>`).join('');

    const linkHtml = device.url
      ? `<a href="${esc(device.url)}" class="pos-compatibility-row__learn-more-link link--underline">${esc(i18n.viewDetails)}</a>`
      : '';

    this.innerHTML = `
      <div class="pos-compatibility-row__image-cell" role="cell">${imageHtml}</div>
      <div class="pos-compatibility-row__name-cell" role="cell">
        <span class="pos-compatibility-row__name">${esc(device.title ?? '')}</span>
        ${verifiedHtml}
      </div>
      <div class="pos-compatibility-row__brand-cell" role="cell" data-label="${esc(i18n.headerBrand)}">
        ${esc(device.vendor ?? '')}
      </div>
      <div class="pos-compatibility-row__connectivity-cell" role="cell" data-label="${esc(i18n.headerConnectivity)}">
        ${connectivityItems ? `<ul class="pos-compatibility-row__connectivity-list">${connectivityItems}</ul>` : '—'}
      </div>
      <div class="pos-compatibility-row__features-cell" role="cell" data-label="${esc(i18n.headerFeatures)}">
        ${featuresItems ? `<ul class="pos-compatibility-row__features-list">${featuresItems}</ul>` : '—'}
      </div>
      <div class="pos-compatibility-row__actions-cell" role="cell">${linkHtml}</div>
    `;
  }
}

customElements.define('pos-compatibility-row', PosCompatibilityRow);

/**
 * Renders one type group: heading, column headers, and device rows.
 * Responds to pos-compat:search and pos-compat:filter document events.
 */
class PosCompatibilityTable extends HTMLElement {
  #type;
  #i18n;
  #rows = [];
  #lastQuery = '';
  #ac;

  /**
   * Sets the device type and renders the table. Must be called before insertion.
   * @param {string} type - Device type label (e.g. "Printers").
   * @param {Object[]} devices - Array of device objects for this type.
   * @param {Object} i18n - Localisation strings.
   * @param {string} [description] - Optional descriptive text shown below the type heading.
   */
  init(type, devices, i18n, description = '') {
    this.#type = type;
    this.#i18n = i18n;
    this.#render(devices, description);
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    document.addEventListener('pos-compat:search', e => {
      this.#lastQuery = e.detail.query;
      if (!this.hidden) this.#applySearch(e.detail.query);
    }, { signal });

    document.addEventListener('pos-compat:filter', e => {
      const { types } = e.detail;
      const filtered = types.length > 0 && !types.includes(this.#type);
      this.hidden = filtered;
      if (!filtered) this.#applySearch(this.#lastQuery);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /**
   * Builds the type heading, column header row, and device rows, then appends them.
   * Devices are sorted: verified first, then those with links, then alphabetically by title.
   * @param {Object[]} devices
   * @param {string} description
   */
  #render(devices, description) {
    const sorted = [...devices].sort((a, b) => {
      if (a.verified !== b.verified) return a.verified ? -1 : 1;
      if (!!a.url !== !!b.url) return a.url ? -1 : 1;
      return (a.title ?? '').localeCompare(b.title ?? '');
    });

    const count = sorted.length;
    const countText = (count === 1 ? this.#i18n.countOne : this.#i18n.countOther).replace('{n}', count);

    const heading = document.createElement('div');
    heading.className = 'pos-compatibility-table__type-heading';
    heading.innerHTML = `
      <div class="pos-compatibility-table__type-title-row">
        <h2 class="pos-compatibility-table__type-name">${esc(this.#type)}</h2>
        <span class="pos-compatibility-table__type-count" data-total="${count}">${esc(countText)}</span>
      </div>
      ${description ? `<p class="pos-compatibility-table__type-description">${esc(description)}</p>` : ''}
    `;
    this.appendChild(heading);

    const section = document.createElement('section');
    section.setAttribute('role', 'table');
    section.setAttribute('aria-label', this.#type);
    section.className = 'pos-compatibility-table__type-section';
    section.innerHTML = `
      <div class="pos-compatibility-table__table-header" role="row">
        <div role="columnheader" aria-hidden="true"></div>
        <div role="columnheader">${esc(this.#i18n.headerDevice)}</div>
        <div role="columnheader">${esc(this.#i18n.headerBrand)}</div>
        <div role="columnheader">${esc(this.#i18n.headerConnectivity)}</div>
        <div role="columnheader">${esc(this.#i18n.headerFeatures)}</div>
        <div role="columnheader" aria-hidden="true"></div>
      </div>
    `;

    for (const device of sorted) {
      const row = document.createElement('pos-compatibility-row');
      row.init(device, this.#i18n);
      section.appendChild(row);
      this.#rows.push(row);
    }

    this.appendChild(section);
  }

  /**
   * Filters visible rows to those whose search text contains the query,
   * and updates the count label to reflect the number of visible results.
   * @param {string} query - Raw search string (trimmed and lowercased internally).
   */
  #applySearch(query) {
    const q = query.trim().toLowerCase();
    let visible = 0;

    for (const row of this.#rows) {
      const matches = !q || row.dataset.searchText.includes(q);
      row.hidden = !matches;
      if (matches) visible++;
    }

    this.classList.toggle('has-no-search-results', q.length > 0 && visible === 0);

    const countEl = this.querySelector('.pos-compatibility-table__type-count');
    if (countEl) {
      const total = parseInt(countEl.dataset.total, 10);
      const n = q ? visible : total;
      countEl.textContent = (n === 1 ? this.#i18n.countOne : this.#i18n.countOther).replace('{n}', n);
    }
  }
}

customElements.define('pos-compatibility-table', PosCompatibilityTable);

/**
 * Dispatches pos-compat:search on the document when the user types.
 */
class PosCompatibilitySearch extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const input = this.querySelector('input[type="search"]');
    if (!input) return;
    input.addEventListener('input', () => {
      document.dispatchEvent(new CustomEvent('pos-compat:search', {
        detail: { query: input.value }
      }));
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

customElements.define('pos-compatibility-search', PosCompatibilitySearch);

/**
 * Filter checkboxes. Types are populated by pos-compatibility-section
 * via setTypes() once the device data is available.
 */
class PosCompatibilityFilters extends HTMLElement {
  #ac;
  #popover;
  #btn;
  #list;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#popover = this.querySelector('.pos-compatibility-filters__popover');
    this.#btn = this.querySelector('.pos-compatibility-filters__btn');
    this.#list = this.querySelector('.pos-compatibility-filters__list');
    const clearBtn = this.querySelector('.pos-compatibility-filters__clear');

    if (!this.#popover || !this.#btn || !this.#list || !clearBtn) return;

    this.#popover.addEventListener('toggle', e => {
      const isOpen = e.newState === 'open';
      this.toggleAttribute('open', isOpen);
      this.#btn.setAttribute('aria-expanded', isOpen);
    }, { signal });

    this.#list.addEventListener('change', () => this.#dispatch(), { signal });

    clearBtn.addEventListener('click', () => {
      for (const cb of this.querySelectorAll('input[type="checkbox"]')) {
        cb.checked = false;
      }
      this.#dispatch();
      this.#popover.hidePopover();
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /**
   * Renders the list of filter checkboxes from the provided type labels.
   * @param {string[]} types - Ordered list of device type labels.
   */
  setTypes(types) {
    this.#list.innerHTML = types.map(type => `
      <label class="pos-compatibility-filters__item">
        <input type="checkbox" value="${esc(type)}">
        ${esc(type)}
      </label>
    `).join('');
  }

  /**
   * Reads the checked checkboxes and dispatches a pos-compat:filter event.
   */
  #dispatch() {
    const checked = [...this.querySelectorAll('input:checked')].map(el => el.value);
    document.dispatchEvent(new CustomEvent('pos-compat:filter', {
      detail: { types: checked }
    }));
  }
}

customElements.define('pos-compatibility-filters', PosCompatibilityFilters);

/**
 * Renders device type groups into pos-compatibility-collection.
 * Called by pos-compatibility-section once data is parsed.
 */
class PosCompatibilityCollection extends HTMLElement {
  #noResultsEl;
  #ac;

  /**
   * Groups devices by type, creates a PosCompatibilityTable per type in display order,
   * and listens for search/filter events to toggle the empty-state message.
   * @param {Object[]} devices - Full flat list of device objects.
   * @param {Object} i18n - Localisation strings.
   * @param {string[]} orderedTypes - Display order for type groups.
   * @param {Map<string, string>} descriptions - Map of type label → description text.
   */
  init(devices, i18n, orderedTypes, descriptions) {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#noResultsEl = document.createElement('p');
    this.#noResultsEl.className = 'pos-compatibility-collection__no-results';
    this.#noResultsEl.hidden = true;
    this.#noResultsEl.textContent = i18n.noDevicesMatch;
    this.appendChild(this.#noResultsEl);

    const groups = new Map();
    for (const device of devices) {
      const type = (device.type ?? '').toLowerCase();
      if (!groups.has(type)) groups.set(type, []);
      groups.get(type).push(device);
    }

    for (const type of orderedTypes) {
      const typeDevices = groups.get(type.toLowerCase());
      if (!typeDevices) continue;
      const table = document.createElement('pos-compatibility-table');
      table.init(type, typeDevices, i18n, descriptions.get(type.toLowerCase()) ?? '');
      this.insertBefore(table, this.#noResultsEl);
    }

    const updateNoResults = () => {
      const anyVisible = [...this.querySelectorAll('pos-compatibility-table')].some(
        t => !t.hidden && !t.classList.contains('has-no-search-results')
      );
      this.#noResultsEl.hidden = anyVisible;
    };

    document.addEventListener('pos-compat:search', updateNoResults, { signal });
    document.addEventListener('pos-compat:filter', updateNoResults, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

customElements.define('pos-compatibility-collection', PosCompatibilityCollection);

/**
 * Outermost coordinator. Parses the JSON data block, then initialises
 * pos-compatibility-filters and pos-compatibility-collection directly.
 * Defined last so all child custom elements are already upgraded.
 */
class PosCompatibilitySection extends HTMLElement {
  connectedCallback() {
    const scriptEl = this.querySelector('script[type="application/json"]');
    if (!scriptEl) return;

    let parsed;
    try {
      parsed = JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.error('[pos-compatibility-section] Invalid JSON data', e);
      return;
    }

    const { devices, typeGroups = [], i18n } = parsed;

    const descriptions = new Map(typeGroups.map(tg => [tg.type.toLowerCase(), tg.description]));

    const deviceTypesLower = new Set(devices.map(d => d.type?.toLowerCase()).filter(Boolean));
    const sentenceCase = s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

    // Block-defined types (in block order) first, then any remaining device types.
    const orderedTypes = [
      ...typeGroups.filter(tg => deviceTypesLower.has(tg.type.toLowerCase())).map(tg => sentenceCase(tg.type)),
      ...[...deviceTypesLower].filter(lower => !typeGroups.some(tg => tg.type.toLowerCase() === lower)).map(sentenceCase)
    ];

    this.querySelector('pos-compatibility-filters').setTypes(orderedTypes);
    this.querySelector('pos-compatibility-collection').init(devices, i18n, orderedTypes, descriptions);
  }
}

customElements.define('pos-compatibility-section', PosCompatibilitySection);

